import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import Swal from "sweetalert2";
import { useAuth } from "../auth/AuthContext";
import Select from "../components/Select";
import pathwayDescriptions from "../data/pathwayDescriptions.json";

export default function Pathway() {
  const {
    authFetch,
    accessToken,
    bootstrapping,
    user,
    pendingTask,
    pendingTaskState,
    startPendingTask,
    selectedScenathon,
  } = useAuth();

  // data
  const [jsonData, setJsonData] = useState(null);
  const [country, setCountry] = useState(""); // backend: req.user.state
  const [taskid] = useState(4);

  // ✅ pathways guardados en BD
  const [userPathways, setUserPathways] = useState([]);
  const [customScenarios, setCustomScenarios] = useState([]);

  // UI state
  const [loadingPage, setLoadingPage] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showPresetModal, setShowPresetModal] = useState(false);

  // form state
  const [pathwayName, setPathwayName] = useState("");
  const [selectValues, setSelectValues] = useState({});

  const scenarioNamesRef = useRef([]);

  const scenarios = Array.isArray(jsonData?.scenarios) ? jsonData.scenarios : [];

  const hiddenScenarioNamesList = [
    "Calibration Year",
    "GDP",
    "Trade balance",
    "Population activity",
    "Climate change impact",
    "Biofuel demand",
    "Price evolution",
    "Global warming coefficient",
    "Urban expansion",
  ];

  const hiddenScenarioNames = new Set(
    hiddenScenarioNamesList.map((name) => String(name || "").trim().toLowerCase()),
  );

  const normalizedType = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z]/g, "");

  const getScenarioKey = (name) => {
    if (!name) return null;
    const normalized = String(name || "").trim().toLowerCase();
    const exact = Object.keys(pathwayDescriptions).find(
      (key) => key.toLowerCase() === normalized,
    );
    if (exact) return exact;

    return Object.keys(pathwayDescriptions).find((key) =>
      key.toLowerCase().replace(/[^a-z]/g, "") === normalized.replace(/[^a-z]/g, ""),
    );
  };

  const getScenarioDescription = (scenarioName, optionValue) => {
    const key = getScenarioKey(scenarioName);
    if (!key) {
      return optionValue
        ? "Custom scenario, see details in the Scenarios section."
        : "";
    }
    if (!optionValue) return "";
    const descriptions = pathwayDescriptions[key]?.descriptions || {};
    const exactDesc = descriptions[optionValue];
    if (exactDesc) return exactDesc;

    const normalizedOption = String(optionValue || "").trim().toLowerCase();
    const foundOption = Object.keys(descriptions).find(
      (opt) => String(opt || "").trim().toLowerCase() === normalizedOption,
    );
    return foundOption ? descriptions[foundOption] : "";
  };

  const scenarioTypeFromLabel = (label) => {
    const v = normalizedType(label);
    if (v.includes("diet")) return "diet";
    if (v.includes("import")) return "imports";
    if (v.includes("export")) return "exports";
    if (v.includes("protected") || v.includes("proteg")) return "protectedareas";
    if (v.includes("afforest") || v.includes("forest")) return "afforestation";
    return v;
  };

  // Enriquecer opciones base con escenarios custom del usuario por tipo
  const scenariosWithCustomOptions = useMemo(() => {
    const byType = new Map();

    for (const row of Array.isArray(customScenarios) ? customScenarios : []) {
      const t = normalizedType(row?.type_scenario);
      const n = String(row?.name || "").trim();
      if (!t || !n) continue;
      if (!byType.has(t)) byType.set(t, new Set());
      byType.get(t).add(n);
    }

    return (Array.isArray(scenarios) ? scenarios : []).map((sc) => {
      const typeKey = scenarioTypeFromLabel(sc?.name);
      const customNames = Array.from(byType.get(typeKey) || []);
      const base = Array.isArray(sc?.options) ? sc.options : [];
      const merged = [...new Set([...base, ...customNames])];
      return {
        ...sc,
        options: merged,
      };
    });
  }, [scenarios, customScenarios]);

  const visibleScenarios = useMemo(
    () =>
      scenariosWithCustomOptions?.filter(
        (s) => !hiddenScenarioNames.has(String(s.name || "").trim().toLowerCase()),
      ) ?? [],
    [scenariosWithCustomOptions, hiddenScenarioNames],
  );

  const hiddenPresetScenarios = useMemo(() => {
    const actualMap = new Map(
      (scenariosWithCustomOptions || [])
        .filter((s) =>
          hiddenScenarioNames.has(String(s.name || "").trim().toLowerCase()),
        )
        .map((s) => [String(s.name || "").trim().toLowerCase(), s]),
    );

    return hiddenScenarioNamesList.map((name) => {
      const normalized = String(name || "").trim().toLowerCase();
      return actualMap.get(normalized) ?? { name, options: [] };
    });
  }, [scenariosWithCustomOptions, hiddenScenarioNames, hiddenScenarioNamesList]);

  // ✅ Lista combinada (JSON + BD) sin duplicados por name
  const pathwaysList = useMemo(() => {
    const fromJson = Array.isArray(jsonData?.Pathways) ? jsonData.Pathways : [];
    const fromDb = Array.isArray(userPathways) ? userPathways : [];

    const seen = new Set();
    const merged = [];

    // prioriza BD
    for (const p of fromDb) {
      const n = String(p?.name ?? "").trim();
      if (!n || seen.has(n)) continue;
      seen.add(n);
      merged.push(p);
    }
    for (const p of fromJson) {
      const n = String(p?.name ?? "").trim();
      if (!n || seen.has(n)) continue;
      seen.add(n);
      merged.push(p);
    }

    return merged;
  }, [jsonData, userPathways]);

  // ---------------------------------------
  // ✅ Carga defined scenarios + pathways BD
  // ---------------------------------------
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    async function load() {
      try {
        setLoadingPage(true);
        setError("");

        if (bootstrapping) return;

        const res = await authFetch("/api/pathways/definedscenarios", {
          method: "GET",
          signal: controller.signal,
        });

        if (res.status === 401) {
          throw new Error("Session expired. Please sign in again.");
        }

        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || `Error ${res.status}`);

        const loc = data.location || "";
        setCountry(loc);

        const payload = data?.jsonData?.[String(loc).trim().toUpperCase()] ?? null;

        if (!alive) return;

        setJsonData(payload);
        setUserPathways(Array.isArray(data?.pathways) ? data.pathways : []);
        setCustomScenarios(Array.isArray(data?.customScenarios) ? data.customScenarios : []);

        const loadedScenarios = payload?.scenarios ?? [];
        scenarioNamesRef.current = loadedScenarios.map((s) => s.name);

        // restore local storage
        const savedInput = localStorage.getItem("input_pathway");
        setPathwayName(savedInput || "");

        const initialSelects = {};
        for (const s of loadedScenarios) {
          const key = `select_${s.name.replace(/\s+/g, "_")}`;
          const saved = localStorage.getItem(key);
          const defaultVal =
            Array.isArray(s.options) && s.options.length ? s.options[0] : "";
          initialSelects[s.name] = saved || defaultVal;
        }
        setSelectValues(initialSelects);
      } catch (e) {
        if (e?.name !== "AbortError") setError(e?.message || "Unexpected error");
      } finally {
        if (alive && !bootstrapping) setLoadingPage(false);
      }
    }

    load();

    return () => {
      alive = false;
      controller.abort();
    };
  }, [authFetch, bootstrapping]);

  // localStorage sync
  useEffect(() => {
    localStorage.setItem("input_pathway", pathwayName);
  }, [pathwayName]);

  useEffect(() => {
    for (const [name, val] of Object.entries(selectValues)) {
      const key = `select_${name.replace(/\s+/g, "_")}`;
      localStorage.setItem(key, val ?? "");
    }
  }, [selectValues]);

  // autocomplete selects when pathway changes
  useEffect(() => {
    if (!jsonData) return;
    const selectedPathway = pathwaysList.find((p) => p?.name === pathwayName);
    if (!selectedPathway) return;

    setSelectValues(() => {
      const next = {};
      for (const scenario of scenariosWithCustomOptions) {
        next[scenario.name] =
          Array.isArray(scenario.options) && scenario.options.length
            ? scenario.options[0]
            : "";
      }

      for (const [key, value] of Object.entries(selectedPathway)) {
        if (!key) continue;
        if (!Object.prototype.hasOwnProperty.call(next, key)) continue;

        const sc = scenariosWithCustomOptions.find((s) => s.name === key);
        const opts = sc?.options ?? [];
        if (opts.includes(value)) next[key] = value;
      }

      return next;
    });
  }, [pathwayName, jsonData, pathwaysList, scenariosWithCustomOptions]);

  // ---------------------------------------
  // ✅ helper: existe pathway con ese nombre?
  // ---------------------------------------
  const checkPathwayExists = useCallback(
    async ({ name, country }) => {
      const params = new URLSearchParams({ name, country });
      const res = await authFetch(`/api/pathways/exists?${params.toString()}`, {
        method: "GET",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Error ${res.status}`);
      return !!data?.exists;
    },
    [authFetch],
  );

  // ---------------------------------------
  // ✅ Encolar Pathway en tasktodo
  // ---------------------------------------
  const savePathwayViaTasktodo = useCallback(
    async ({ name, colvalue, countryForDb, overwrite }) => {
      const res = await authFetch("/api/sendtask/tasktodo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          task_id: taskid,
          name,
          status: "todo",
          onpc: 1,
          colname: "Pathway",
          colvalue,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (res.status === 401) throw new Error("Session expired. Please sign in again.");
      if (!res.ok) throw new Error(data?.error || `Error creating task (${res.status})`);

      const id = data?.id;
      if (!id) throw new Error("Backend did not return task id.");

      return {
        id,
        ok: true,
        country: countryForDb || country,
        overwrite: !!overwrite,
      };
    },
    [authFetch, taskid, country],
  );

  useEffect(() => {
    const onTaskCompleted = async (event) => {
      const detail = event?.detail || {};
      const taskMeta = detail?.pendingTask || {};
      if (taskMeta?.colname !== "Pathway") return;

      const saved = detail?.pathwaySaved?.pathway;
      if (saved) {
        const uiObj = {
          name: saved?.Name || taskMeta?.name || "",
          ...Object.fromEntries(
            (Array.isArray(saved?.Scenarios) ? saved.Scenarios : [])
              .map((it) => {
                const idx = Number(String(it?.scenario || "").replace(/^S/i, ""));
                const key = scenarios[idx]?.name;
                return [key, String(it?.value ?? "")];
              })
              .filter(([k]) => !!k),
          ),
          __id: detail?.pathwaySaved?.id,
          __updated_at: detail?.pathwaySaved?.updated_at,
        };

        setUserPathways((prev) => {
          const filtered = (Array.isArray(prev) ? prev : []).filter((p) => p?.name !== uiObj.name);
          return [uiObj, ...filtered];
        });

        if (uiObj.name) setPathwayName(uiObj.name);
      }

      localStorage.removeItem("input_pathway");
      (jsonData?.scenarios || []).forEach((scenario) => {
        localStorage.removeItem(`select_${scenario.name.replace(/\s+/g, "_")}`);
      });

      await Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Pathway processed and saved successfully.",
        confirmButtonColor: "#3085d6",
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
    };

    const onTaskFailed = async (event) => {
      const detail = event?.detail || {};
      const taskMeta = detail?.pendingTask || {};
      if (taskMeta?.colname !== "Pathway") return;

      const errMessage = detail?.error || "Error processing pathway task";
      setError(errMessage);

      await Swal.fire({
        icon: "error",
        title: "Task failed",
        text: errMessage,
        confirmButtonColor: "#3085d6",
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
    };

    window.addEventListener("scenathon-task-completed", onTaskCompleted);
    window.addEventListener("scenathon-task-failed", onTaskFailed);

    return () => {
      window.removeEventListener("scenathon-task-completed", onTaskCompleted);
      window.removeEventListener("scenathon-task-failed", onTaskFailed);
    };
  }, [jsonData, scenarios]);

  const insertData = async () => {
    setError("");

    if (bootstrapping) return;

    if (!accessToken) {
      setError("No active session. Please sign in again.");
      return;
    }

    if (!user?.id) {
      setError("Could not get userId from the session.");
      return;
    }

    const nameTrim = String(pathwayName || "").trim();
    if (!nameTrim) {
      await Swal.fire({
        icon: "warning",
        title: "Warning!",
        text: "Pathway is empty",
        confirmButtonText: "OK",
        confirmButtonColor: "#3085d6",
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
      return;
    }

    // 1) revisa duplicado y pregunta si reemplazar o cambiar nombre
    try {
      const exists = await checkPathwayExists({ name: nameTrim, country });
      const isDefinedScenarioPathway = Array.isArray(jsonData?.Pathways)
        ? jsonData.Pathways.some((p) => String(p?.name || "").trim() === nameTrim)
        : false;

      if (exists && isDefinedScenarioPathway) {
        await Swal.fire({
          icon: "warning",
          title: `Cannot replace "${nameTrim}"`,
          text: `The selected pathway "${nameTrim}" is a predefined scenario and already exists. Please rename the pathway and try again.`,
          confirmButtonText: "OK",
          confirmButtonColor: "#3085d6",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });
        return;
      }

      let overwrite = false;

      if (exists) {
        const choice = await Swal.fire({
          icon: "warning",
          title: "Pathway already exists",
          text: `A pathway named "${nameTrim}" already exists. Do you want to replace it or change the name?`,
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: "Replace",
          denyButtonText: "Change name",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#3085d6",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        if (choice.isDenied) return; // usuario quiere cambiar el nombre
        if (!choice.isConfirmed) return; // cancel

        overwrite = true;
      }

      // 2) confirm general
      const confirm = await Swal.fire({
        title: "Are you sure?",
        text: "Do you want to submit your data?\nClick Confirm to continue or Cancel to review.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#3085d6",
        allowOutsideClick: false,
        allowEscapeKey: false,
      });

      if (!confirm.isConfirmed) return;

      // 3) encolar tarea
      setIsSubmitting(true);

      const scenariosArr = scenariosWithCustomOptions.map((s, index) => ({
        scenario: `S${index}`,
        value: String(selectValues[s.name] ?? ""),
      }));

      const colvalue = {
        pathway: {
          Name: nameTrim,
          Scenarios: scenariosArr,
          scenathonId: selectedScenathon?.id || undefined,
        },
      };

      const resp = await savePathwayViaTasktodo({
        name: nameTrim,
        colvalue,
        countryForDb: country,
        overwrite,
      });

      startPendingTask({
        id: resp.id,
        name: nameTrim,
        country: resp.country || country,
        colname: "Pathway",
        overwrite: resp.overwrite,
      });

      await Swal.fire({
        icon: "info",
        title: "Task sent",
        text: "Your pathway is being processed in background. You can navigate or refresh safely.",
        confirmButtonColor: "#3085d6",
        allowOutsideClick: false,
        allowEscapeKey: false,
      });

      // ✅ Si quieres que se vea “de inmediato” en esta sección, NO redirijas.
      // window.location.href = "/resultados";
    } catch (err) {
      // si el backend dice que existe (carrera), también lo manejamos
      if (err?.code === "PATHWAY_EXISTS") {
        await Swal.fire({
          icon: "warning",
          title: "Pathway already exists",
          text: "That pathway already exists. Change the name or choose to replace it.",
          confirmButtonColor: "#3085d6",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });
        return;
      }
      console.error("Error guardando pathway:", err);
      setError(err?.message || "Error sending data");
    } finally {
      setIsSubmitting(false);
    }
  };
  const pathwayInvalid = /^\d/.test(pathwayName);
  const hasPathwayTaskPending = pendingTask?.colname === "Pathway";
  const isType2User = Number(user?.type_id) === 2 || Number(user?.type_id) === 4;
  const sendDataDisabled =
    pathwayInvalid ||
    isType2User ||
    loadingPage ||
    bootstrapping ||
    isSubmitting ||
    hasPathwayTaskPending;
  const pathwayBusyLabel = hasPathwayTaskPending
    ? "Processing Pathway..."
    : "Done...";

  return (
    <div id="app" className="w-full mt-8">
      <main className="flex-fill home">
        <div className="mx-auto w-full">
          {hasPathwayTaskPending && (
            <div role="status" aria-live="polite" className="mb-4 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
              {`Pathway task #${pendingTask.id} is processing (${pendingTaskState || "pending"}). You can keep working while it finishes.`}
              <div className="mt-3 flex w-full max-w-sm items-center gap-1" aria-hidden="true">
                <span className="h-1 flex-1 animate-pulse rounded-full bg-blue-300" />
                <span
                  className="h-1 flex-1 animate-pulse rounded-full bg-blue-400"
                  style={{ animationDelay: "0.15s" }}
                />
                <span
                  className="h-1 flex-1 animate-pulse rounded-full bg-blue-500"
                  style={{ animationDelay: "0.3s" }}
                />
              </div>
            </div>
          )}

          {error && (
            <div role="alert" aria-live="assertive" className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              {error}
            </div>
          )}

          <div className="tabs-container mb-4 space-y-4">
            {loadingPage ? (
              <>
                <SkeletonCard />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
                  <SkeletonCard />
                  <SkeletonCard />
                  <SkeletonCard />
                  <SkeletonCard className="hidden lg:block" />
                  <SkeletonCard className="hidden lg:block" />
                </div>
              </>
            ) : (
              <>
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex flex-col gap-1">

                    <h5 className="text-lg font-semibold text-slate-900">
                      Pathway Name
                    </h5>
                  </div>

                  <input
                    type="text"
                    aria-label="Pathway name"
                    className={`mt-4 w-full rounded-xl border px-3 py-2 text-sm outline-none transition ${pathwayInvalid
                        ? "border-red-500 bg-red-50"
                        : "border-slate-200 bg-slate-50 focus:border-slate-400 focus:bg-white"
                      }`}
                    placeholder="Type or Select"
                    list="datalist-pathways"
                    value={pathwayName}
                    onChange={(e) => setPathwayName(e.target.value)}
                  />

                  {pathwayInvalid && (
                    <p className="mt-1 text-xs text-red-500">
                      Pathway name cannot start with a number.
                    </p>
                  )}

                  <datalist id="datalist-pathways">
                    {(pathwaysList || []).map((p) => (
                      <option key={p.__id ?? p.name} value={p.name} />
                    ))}
                  </datalist>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Scenario Inputs
                      </span>
                      <h5 className="text-lg font-semibold text-slate-900">
                        Configure the rest of the pathway
                      </h5>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowPresetModal(true)}
                      className="inline-flex items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      Preset scenarios
                    </button>
                  </div>

                  <div
                    id="optionsContainer"
                    className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5"
                  >
                    {visibleScenarios.map((scenario) => {
                      const label = scenario.name;
                      const title = window?.i18nTranslations?.pathway?.[label] || label;

                      return (
                        <div
                          key={label}
                          className="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200"
                        >
                          <h5 className="text-center text-base font-semibold text-slate-900">
                            {title}
                          </h5>

                          <Select
                            value={selectValues[label] ?? ""}
                            onChange={(v) => setSelectValues((prev) => ({ ...prev, [label]: v }))}
                            options={(scenario.options || []).map((o) => ({
                              value: o,
                              label: o,
                              description: getScenarioDescription(label, o),
                            }))}
                            placeholder="Select..."
                            disabled={isSubmitting}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="mt-8 text-center">
            <button
              onClick={insertData}
              disabled={sendDataDisabled}
              aria-busy={isSubmitting || hasPathwayTaskPending}
              aria-label={isSubmitting || hasPathwayTaskPending ? pathwayBusyLabel : "Send Data"}
              className={`inline-flex items-center justify-center rounded-2xl px-6 py-3 text-lg font-semibold text-white shadow-lg transition ${sendDataDisabled ? "cursor-not-allowed bg-slate-400" : "bg-[#03203d] hover:bg-[#54B046]"}`}
            >
              {isSubmitting || hasPathwayTaskPending ? (
                <span className="flex flex-col items-center gap-2 leading-none">
                  <span className="inline-flex items-center gap-2">
                    <svg
                      className="h-4 w-4 animate-spin text-white"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.35" strokeWidth="3" />
                      <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                    <span>{pathwayBusyLabel}</span>
                  </span>
                  <span className="flex w-40 items-center gap-1" aria-hidden="true">
                    <span className="h-1 flex-1 animate-pulse rounded-full bg-white/35" />
                    <span
                      className="h-1 flex-1 animate-pulse rounded-full bg-white/55"
                      style={{ animationDelay: "0.15s" }}
                    />
                    <span
                      className="h-1 flex-1 animate-pulse rounded-full bg-white/75"
                      style={{ animationDelay: "0.3s" }}
                    />
                  </span>
                </span>
              ) : (
                "Send Data"
              )}
            </button>
          </div>
        </div>
        {showPresetModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl">
              <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Preset scenarios</h2>
                  <p className="mt-1 text-sm text-slate-600">
                    These scenarios are shown for reference and cannot be changed here.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPresetModal(false)}
                  className="rounded-full bg-slate-100 p-2 text-slate-700 hover:bg-slate-200"
                  aria-label="Close preset scenarios"
                >
                  ✕
                </button>
              </div>
              <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
                {hiddenPresetScenarios.length === 0 ? (
                  <div className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-700">
                    No preset scenarios are available at this time.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {hiddenPresetScenarios.map((scenario) => {
                      const label = scenario.name;
                      const title = window?.i18nTranslations?.pathway?.[label] || label;
                      return (
                        <div key={label} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="mb-3 flex items-center justify-between gap-4">
                            <h3 className="text-base font-semibold text-slate-900">{title}</h3>
                            <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">Preset</span>
                          </div>
                          {Array.isArray(scenario.options) && scenario.options.length > 0 ? (
                            <Select
                              value={selectValues[label] ?? ""}
                              onChange={() => { }}
                              options={(scenario.options || []).map((o) => ({
                                value: o,
                                label: o,
                                description: getScenarioDescription(label, o),
                              }))}
                              placeholder="Not selectable"
                              disabled={true}
                            />
                          ) : (
                            <p className="text-sm text-slate-600">No preset values available.</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="flex justify-end border-t border-slate-200 px-6 py-4">
                <button
                  type="button"
                  onClick={() => setShowPresetModal(false)}
                  className="rounded-2xl bg-slate-900 px-5 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function SkeletonCard({ className = "" }) {
  return (
    <div className={`rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200 ${className}`}>
      <div className="h-4 w-2/3 animate-pulse rounded bg-slate-200" />
      <div className="mt-3 h-3 w-full animate-pulse rounded bg-slate-100" />
      <div className="mt-2 h-3 w-5/6 animate-pulse rounded bg-slate-100" />
      <div className="mt-2 h-3 w-4/6 animate-pulse rounded bg-slate-100" />
    </div>
  );
}